import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Box, TextField } from '@mui/material';
import { Edit, Block, Settings, DesktopMacOutlined, Close, Search, Send } from '@mui/icons-material';
import axios from 'axios.js';
import { useToast } from '../../../hooks/useToast';
import { NavLink, useHistory } from 'react-router-dom';
import { useToastify } from 'app/hooks/useToastify';
import { AxiosResponse } from 'axios';
import { twMerge } from 'tailwind-merge';
import moment from 'moment';
import { maskCelular, maskCpf } from 'functions';
import { Pagination } from "app/components/enotifica/pagination";
import useAuth from 'app/hooks/useAuth';
import { Button } from 'app/components';

interface UserProps {
  id: string;
  name: string;
  cpf: string;
  email: string;
  celular: string;
  perfil?: any;
  access?: any[];
  bloqueado: boolean;
  deletedAt: string | null;
}

function UserList() {
  const [users, setUsers] = useState<UserProps[]>([]);
  const toasty = useToastify();
  const history = useHistory()
  const [size, setSize] = useState(6);
  const [length, setLength] = useState(0);
  const [page, setPage] = useState(1);
  const [maxPages, setMaxPages] = useState(1);
  const [search, setSearch] = useState("");

  const { permissions: userPermissions } = useAuth();
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    const permsProfile = userPermissions?.filter(pem => pem.resource.name == 'users') ?? [];

    if (permsProfile.length > 0) {
      const profilePermissions: string[] = permsProfile?.map(pem => pem.action.name);
      setPermissions(profilePermissions)
    }
  },[]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleChangePage  = (event, value) => {
    setPage(value);
  };

  const handleChangeSize = (e) => {
    setSize(e.target.value);
  }

  useEffect(()=> {
    fetchUsers();
  }, [size, page]);


  async function fetchUsers() {
    try {

      let params:any = {
        size,
        page
      }

      if (search) {
        params = {
          search,
          size,
          page
        }
      }

      const response = await axios.get('/users/admin', { params });
      if (response.status === 200) {
        setUsers(response.data.users);
        setLength(response.data?.count);
        setMaxPages(Math.ceil(response.data?.count/ Number(size)));
      }
    } catch (e) {
      console.error(e);
    }
  }

  const handlePressCadastrar = () => {
    history.push("/usuario/novo")
  }

  const handleBlock = async (userId: string, bloqueado: boolean) => {
    try {
      const response = await axios.put(`/users/admin/${userId}`, {
        bloqueado: !bloqueado
      });

      if (response.status !== 204) {
        toasty.show(
          'error',
          bloqueado ? 'Não foi possível desbloquear o usuário!' : 'Não foi possível bloquear o usuário!'
        );
        throw new Error(response.data?.message ?? response.statusText);
      }

      toasty.show(
        'success',
        bloqueado ? 'Usuário desbloqueado com sucesso!' : 'Usuário bloqueado com sucesso!'
      );

      fetchUsers();

    } catch (e) {
      console.error(e);
    }
  }

  const handleRemoveActive = async (userId: string, removed: boolean) => {
    try {

      let response: AxiosResponse<any, any>;

      if (removed) {
        response = await axios.put(`/users/admin/${userId}/active`);
      } else {
        response = await axios.delete(`/users/admin/${userId}`);
      }

      if (response.status !== 200) {
        toasty.show(
          'error',
          removed ? 'Não foi possível ativar o usuário!' : 'Não foi possível remover o usuário!'
        );
        throw new Error(response.data?.message ?? response.statusText);
      }

      toasty.show(
        'success',
        removed ? 'Usuário ativado com sucesso!' : 'Usuário removido com sucesso!'
      );

      fetchUsers();
    } catch (e) {
      console.error(e);
    }
  }

  async function resendMail(userId: string) {
    try {
      const response = await axios.get(`/users/admin/${userId}/resend-mail`);
      if (response.status === 204) {
        toasty.show(
          'success',
          'E-mail enviado com sucesso!'
        );
      }
    } catch (e) {
      console.error(e);
    }
  }

  return (
    <Box className="tw-w-full tw-flex tw-flex-col tw-gap-10">
      <Box className="tw-p-6 tw-bg-white tw-shadow-md tw-rounded-lg">
        <h2 className="tw-text-xl tw-font-bold tw-mb-6">Pesquisar Usuários</h2>
        <Box className="tw-w-full tw-flex tw-justify-between">
          <TextField 
            label="Pesquisa com palavra-chave"
            size='small'
            value={search}
            onChange={e => setSearch(e.target.value)}
            className='tw-w-[400px]'
            type='text'
            onKeyUp={e => {
              if (e.key === 'Enter') {
                fetchUsers();
              }
            }}
            InputProps={{
              endAdornment: (
                  <Search 
                    className='tw-cursor-pointer'
                    onClick={() => {
                      fetchUsers();
                    }}
                  />
              )
            }}
          />
          <Button.Root>
            <Button.Element 
              onClick={handlePressCadastrar}
              className={twMerge(
                "tw-h-[40px]", 
                !permissions.includes('create') && "tw-bg-slate-400 tw-text-white tw-min-w-[200px] tw-max-w-[230px] hover:tw-cursor-not-allowed tw-rounded-[4px]"
              )}
              disabled={!permissions.includes('create')}
              variant={!permissions.includes('create') ? 'none' : 'primary'}
            >
                <Button.Text text='Cadastrar' />
            </Button.Element>
          </Button.Root>
        </Box>
      </Box>
      <TableContainer component={Paper} className="tw-p-6 tw-bg-white tw-shadow-md tw-rounded-lg">
        <h2 className="tw-text-xl tw-font-bold tw-mb-4">Usuários Cadastrados</h2>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>CPF</TableCell>
              <TableCell>E-mail</TableCell>
              <TableCell>Telefone</TableCell>
              <TableCell>Perfil</TableCell>
              <TableCell>Setor</TableCell>
              <TableCell>Último acesso</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users && users.length > 0 && users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.cpf ? maskCpf(user.cpf) : ""}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.celular ? maskCelular(user.celular) : ""}</TableCell>
                <TableCell>{user.perfil?.name}</TableCell>
                <TableCell>{"-"}</TableCell>
                <TableCell>{user.access && user.access?.length > 0 ? moment(user.access[0]?.accessAt).format("DD/MM/YYYY HH:mm") : ''}</TableCell>
                <TableCell>
                  <IconButton title={!!user.deletedAt ? "Ativar" : "Inativar"} color="default" onClick={() => handleRemoveActive(user.id, !!user.deletedAt)} disabled={!permissions.includes('delete')}>
                    <Close className={twMerge("", !!user.deletedAt && 'tw-text-[#E13441]')} />
                  </IconButton>
                  <IconButton title={user.bloqueado ? "Desbloquear" : "Bloquear"} color="default" onClick={() => handleBlock(user.id, user.bloqueado)} disabled={!permissions.includes('edit')}>
                    <Block className={twMerge("", user.bloqueado && 'tw-text-[#E13441]')} />
                  </IconButton>
                  <IconButton title="Editar" color="default" onClick={() => history.push(`/usuario/edit/${user.id}`)} disabled={!permissions.includes('edit')}>
                    <Edit />
                  </IconButton>
                  <IconButton title="Reenviar e-mail" color="default" onClick={() => resendMail(user.id)} disabled={!permissions.includes('edit')}>
                    <Send />
                  </IconButton>
                  <IconButton title="Log" color="default" onClick={() => history.push(`/usuario/log/${user.id}`)}>
                    <DesktopMacOutlined />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Pagination.Root className="flex flex-row mt-6 justify-end">
          <Pagination.PeerPage className="mr-5" value={size} handleChange={handleChangeSize}></Pagination.PeerPage>
          <Pagination.Element count={maxPages} page={page} onChange={handleChangePage} ></Pagination.Element>
        </Pagination.Root>
      </TableContainer>
    </Box>
  );
}

export default UserList;
